import axios from "axios";
import { default as LogHandler, LogType } from "@/helpers/utilities/Handlers/LogHandler";
import TrackHelper from "@/helpers/utilities/TrackHelper";
import { SessionHelper } from "../utilities/sessionHelper";

import msalConnection from "@/helpers/api/msalConnection";

var apiBaseUrl;
var apiBaseUrlNew;
var apiBaseUrlOnPrem;
var axiosInstance;
var axiosInstanceInitTime = 0;
var axiosInstanceNew;
var axiosInstanceInitTimeNew = 0;
var axiosOnPremInstance;
var axiosOnPremInstanceInitTime = 0;
var getRequestsMap = {};

const pathsToMoveToNewVersion = () => {
  return [
    /\/users\/.*\/photos\/48x48\/\$value/i,
    /\/me\/drive/i, // /me/drive
    /\/me\/drive\/root\/\?\$expand=children/i, // /me/drive/root/?$expand=children
    /\/groups\/\w{8}-\w{4}-\w{4}-\w{4}-\w{12}\/drives\?\$select=id,name,createdBy,driveType,lastModifiedDateTime,webUrl/i, // /groups/dd2b1b04-28a4-4bbc-b855-ceb9e22290f0/drives?$select=id,name,createdBy,driveType,lastModifiedDateTime,webUrl
    /\/drives/
  ];
};

/**
 * Get request to Microsoft Graph API
 * @param { string } path path to the resource
 * @param { object } config axios config
 * @returns { Promise } Promise object represents the response data
 */
export async function graphGet(path, config) {
  if (!getRequestsMap[path]) {
    getRequestsMap[path] = doGraphGet(path, config);
  }
  return getRequestsMap[path];
}

/**
 * Get request to Microsoft Graph API - actually do the request
 * @param { string } path path to the resource
 * @param { object } config axios config
 * @returns { Promise } Promise object represents the response data
 */
async function doGraphGet(path, config) {
  const graphAxios = await initGraphApi(path);
  if (!config) {
    config = {};
  }

  if (!config.headers) {
    config.headers = {
      "Content-Type": "application/json"
    };
  }

  return new Promise((resolve, reject) => {
    graphAxios
      .get(path, config)
      .then(response => {
        resolve(response.data);
      })
      .catch(error => {
        if (error.response) {
          if (error.response.status === 429) {
            let retryAfter = 5;
            if (error.response.headers["retry-after"]) {
              retryAfter = error.response.headers["retry-after"];
            }

            TrackHelper.registerError(error, error.response.headers);

            return delay(retryAfter * 1000).then(() => {
              doGraphGet(path, config).then(result => resolve(result));
            });
          } else {
            reject(error);
          }
        } else {
          reject(error);
        }
      })
      .finally(() => {
        getRequestsMap[path] = null;
      });
  });
}

export default {
  async get(path, config) {
    const graphAxios = await initGraphApi(path);
    if (!graphAxios) {
      throw Error("Graph Api not initialized yet");
    }

    if (!getRequestsMap[path]) {
      if (!config) {
        config = {};
      }

      if (!config.headers) {
        config.headers = {
          "Content-Type": "application/json"
        };
      }
      const request = graphAxios.get(path, config).finally(() => {
        getRequestsMap[path] = null;
      });

      getRequestsMap[path] = request;
    }
    return getRequestsMap[path];
  },

  async post(path, data, config) {
    const graphAxios = await initGraphApi(path);
    if (!graphAxios) {
      throw Error("Graph Api not initialized yet");
    }

    if (!config) {
      config = {};
    }

    if (!config.headers) {
      config.headers = {
        "Content-Type": "application/json"
      };
    }

    return await graphAxios.post(path, data, config);
  },

  async put(path, data, config) {
    const graphAxios = await initGraphApi(path);
    if (!graphAxios) {
      throw Error("Graph Api not initialized yet");
    }
    if (!config) {
      config = {};
    }

    if (!config.headers) {
      config.headers = {
        "Content-Type": "application/json"
      };
    }
    return await graphAxios.put(path, data, config);
  },

  async patch(path, data, config) {
    const graphAxios = await initGraphApi(path);
    if (!graphAxios) {
      throw Error("Graph Api not initialized yet");
    }
    if (!config) {
      config = {};
    }

    if (!config.headers) {
      config.headers = {
        "Content-Type": "application/json"
      };
    }
    return await graphAxios.patch(path, data, config);
  },

  async delete(path, config) {
    const graphAxios = await initGraphApi(path);
    if (!graphAxios) {
      throw Error("Graph Api not initialized yet");
    }
    return await graphAxios.delete(path, config);
  },

  async spread(fns) {
    const graphAxios = await initGraphApi();
    return graphAxios.spread(fns);
  },

  initGraphApi(baseUrl, baseUrlNew) {
    apiBaseUrl = baseUrl;
    apiBaseUrlNew = baseUrlNew;
  },

  initOnPremApi(baseUrl) {
    apiBaseUrlOnPrem = baseUrl;
  },

  signOut() {
    signOutOfMsal();
  }
};

/**
 * Sign out msal connection
 */
function signOutOfMsal() {
  msalConnection.signOut();
}

/**
 *
 * @param { string } path Path to api endpoint
 * @returns { object } Axios instance for the correct version of the graph api { axiosInstance | axiosInstanceNew }
 */
async function initGraphApi(path) {
  if (!apiBaseUrlOnPrem) {
    if (path && pathsToMoveToNewVersion().some(rx => rx.test(path))) {
      //TODO: Slowly move to new v.1 of graph api - this should be temporary
      return await initGraphV1Connection();
    } else {
      return await initGraphBetaConnection();
    }
  } else {
    return await initOnPremApi();
  }
}

/**
 * Create an axios instance for old beta endpoints
 * @returns { object } axios instance for beta version of graph api
 */
async function initGraphBetaConnection() {
  if (!axiosInstance || axiosInstanceInitTime < new Date().getTime() - 3000000) {
    try {
      const graphAxios = axios.create({
        baseURL: apiBaseUrl
      });

      const tokenObject = await fetchGraphToken();

      graphAxios.defaults.headers.common["Authorization"] = `Bearer ${tokenObject.accessToken}`;
      axiosInstance = graphAxios;
    } catch (error) {
      TrackHelper.registerError(error);
      LogHandler.log(LogType.ERROR, "graphAxios", error);
    }
  }
  return axiosInstance;
}

/**
 * Create an axios instance for the new v.1 of graph api
 * @returns { object } axios instance for v1 version of graph api
 */
async function initGraphV1Connection() {
  if (!axiosInstanceNew || axiosInstanceInitTimeNew < new Date().getTime() - 3000000) {
    try {
      const graphAxios = axios.create({
        baseURL: apiBaseUrlNew
      });

      const tokenObject = await fetchGraphToken();

      if (tokenObject) {
        graphAxios.defaults.headers.common["Authorization"] = `Bearer ${tokenObject.accessToken}`;
        axiosInstanceNew = graphAxios;
      }
    } catch (error) {
      TrackHelper.registerError(error);
      LogHandler.log(LogType.ERROR, "graphAxios", error);
    }
  }
  return axiosInstanceNew;
}

/**
 * Fetch token object from msal login
 * @returns { object } Token object from msal
 */
async function fetchGraphToken() {
  const request = {
    account: msalConnection.msalAccount,
    scopes: ["https://graph.microsoft.com/.default"]
  };

  let tokenObject;

  try {
    tokenObject = await msalConnection.msalInstance.acquireTokenSilent(request);
  } catch {
    if (window.parent !== window) {
      // We are inside an iFrame
      try {
        tokenObject = await msalConnection.msalInstance.acquireTokenPopup(request);
      } catch {
        SessionHelper.requirePopupLogin();
      }
    } else {
      tokenObject = await msalConnection.msalInstance.acquireTokenRedirect(request);
    }
  }
  return tokenObject;
}

/**
 * Force popup for authenticating graph connection
 * @returns { Promise<object> } Promise from login request
 */
export async function forceGraphPopupConnection() {
  const request = {
    account: msalConnection.msalAccount,
    scopes: ["https://graph.microsoft.com/.default"]
  };

  return await msalConnection.msalInstance.acquireTokenPopup(request);
}

/**
 * Create Axios instance for on-prem environment
 * @returns {object} axios instance
 */
function initOnPremApi() {
  if (!axiosOnPremInstance || axiosOnPremInstanceInitTime < new Date().getTime() - 3000000) {
    axiosOnPremInstance = axios.create({
      baseURL: apiBaseUrlOnPrem
    });

    axiosOnPremInstance.interceptors.request.use(config => {
      config.headers["Authorization"] = "Bearer " + SessionHelper.nctSessionToken();
      config.headers["RouteName"] = "home";
      return config;
    });
  }
  return axiosOnPremInstance;
}

/**
 * Simple delay function
 * @param { number } t - time in milliseconds
 * @returns { Promise } - promise
 */
function delay(t) {
  return new Promise(resolve => setTimeout(resolve, t));
}
